import { Alert, Box } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useParams } from 'react-router-dom';

import { CHANGE_ORDER_REASON_LABELS, ChangeOrderDocument, ChangeOrderStatus } from 'types';

import { useGetChangeOrdersQuery } from '../../services/changeOrders';
import { useGetDocumentByAccountAndDocumentIdQuery } from '../../services/documents';
import DateTimeDisplay from '../DateTime';
import { FileItem } from '../documents/FileItem';
import FormSection from '../Forms/FormSection';

/**
 * One executed change order: its amended figures plus the amendment PDF.
 *
 * The amendment is generated asynchronously after execution, so `amendmentDocumentId` is briefly
 * absent on a freshly executed change order. That reads as "being generated" rather than as a missing
 * document — a reviewer who opens the package in that window should not conclude the paperwork is
 * lost.
 */
const ExecutedChangeOrderItem = ({
  changeOrder,
  accountId,
}: {
  changeOrder: ChangeOrderDocument;
  accountId: string;
}) => {
  const amendmentDocumentId = changeOrder.amendmentDocumentId ? String(changeOrder.amendmentDocumentId) : undefined;
  const { data: amendmentDocument } = useGetDocumentByAccountAndDocumentIdQuery(
    { accountId, documentId: amendmentDocumentId as string },
    { skip: !accountId || !amendmentDocumentId },
  );
  const amendmentFile = amendmentDocument?.files?.[0];

  return (
    <Box childGap="xs" padding="0 0 md 0">
      <Box direction="row" childGap="md" fontSize="sm">
        <Box fontWeight="medium">{`Change Order ${changeOrder.sequence ?? ''}`.trim()}</Box>
        {changeOrder.initiatedBy?.submittedAt && (
          <DateTimeDisplay value={changeOrder.initiatedBy.submittedAt} fontSize="sm" dateFormat="DD" inline />
        )}
      </Box>
      {changeOrder.reasons?.length > 0 && (
        <Box fontSize="xs" color="body-secondary">
          {`Reason${changeOrder.reasons.length > 1 ? 's' : ''}: `}
          {changeOrder.reasons.map((reason) => CHANGE_ORDER_REASON_LABELS[reason]).join(', ')}
        </Box>
      )}
      {amendmentFile ? (
        <FileItem file={amendmentFile} />
      ) : (
        <Box fontSize="xs" color="body-secondary">
          Amendment document is being generated.
        </Box>
      )}
    </Box>
  );
};

/**
 * Surfaces executed change orders in the install package alongside the customer contract, so a
 * reviewer sees that the contract they are looking at has been amended and can open the amendment
 * itself. Without this the section reads "Customer Contract" and shows only the original signed PDF,
 * which no longer states the terms the account is on.
 *
 * Renders nothing when no change order has executed, so accounts without one are unaffected.
 */
export const ChangeOrderDocumentSection = ({ showSeparatorBorder }: { showSeparatorBorder?: boolean }) => {
  const { id: accountId } = useParams<{ id: string }>();
  // Gated on easy-change-order, matching how ContractList gates its change-order UI.
  const { easyChangeOrder = false } = useFlags();
  const { data: changeOrders } = useGetChangeOrdersQuery(accountId as string, {
    skip: !accountId || !easyChangeOrder,
  });

  const executedChangeOrders = (changeOrders ?? []).filter(
    (changeOrder) => changeOrder.status === ChangeOrderStatus.executed,
  );

  if (!accountId || executedChangeOrders.length === 0) {
    return null;
  }

  return (
    <FormSection
      title="Change Orders"
      description="Executed change orders amend the customer contract. The amended terms — not the original contract's — are what this install package is reviewed against."
      className={!showSeparatorBorder ? 'border-out' : ''}
    >
      <Box childGap="md">
        <Alert
          variant="warning"
          hasIcon
          title={
            executedChangeOrders.length === 1
              ? 'This contract has been amended by a change order'
              : `This contract has been amended by ${executedChangeOrders.length} change orders`
          }
          message="Review the amendment below for the current contracted terms."
        />
        {executedChangeOrders.map((changeOrder) => (
          <ExecutedChangeOrderItem key={String(changeOrder.id)} changeOrder={changeOrder} accountId={accountId} />
        ))}
      </Box>
    </FormSection>
  );
};
